import React, { useState, useEffect } from "react";
import AdminSidebar from "./AdminSidebar";
import AdminTopBar from "./AdminTopBar";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import api from "./api/axios";
import { useActivityLogger } from "./hooks/useActivity";
export default function AdminAddDokter() {
  const navigate = useNavigate();
  const { logAdminActivity } = useActivityLogger();

  // Form data state
  const [formData, setFormData] = useState({
    nama: "",
    consultationUrl: "",
    selectedFaskes: [],
  });

  // Map and location states
  const [coords, setCoords] = useState({ lat: -6.2, lng: 106.816666 });
  const [address, setAddress] = useState("");
  const [mapObj, setMapObj] = useState(null);

  // Schedule state
  const [schedules, setSchedules] = useState([{ day: "", start: "", end: "" }]);
  const days = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];

  // UI states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [faskesList, setFaskesList] = useState([]);
  const [loadingFaskes, setLoadingFaskes] = useState(true);

  // Fetch available faskes for selection
  useEffect(() => {
    const fetchFaskes = async () => {
      try {
        setLoadingFaskes(true);
        const response = await api.get("/faskes");
        const faskesData = response.data.results || response.data || [];
        setFaskesList(faskesData);
      } catch (err) {
        console.error("Error fetching faskes:", err);
        setError("Gagal memuat daftar faskes");
      } finally {
        setLoadingFaskes(false);
      }
    };
    fetchFaskes();
    logAdminActivity(
      "view",
      "view_add_dokter",
      "admin_add_dokter",
      "admin_add_dokter",
      "Admin membuka halaman tambah dokter",
      "Halaman untuk menambah dokter baru ke sistem",
      "Tambah Dokter Baru"
    );
  }, []);

  // Schedule handlers
  const handleAddSchedule = () =>
    setSchedules([...schedules, { day: "", start: "", end: "" }]);

  const handleRemoveSchedule = (index) =>
    setSchedules(schedules.filter((_, i) => i !== index));

  const handleScheduleChange = (index, field, value) => {
    const updated = schedules.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setSchedules(updated);
  };

  // Form input handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (error) setError("");
  };

  const handleFaskesSelection = (e) => {
    const selectedOptions = Array.from(e.target.selectedOptions).map(
      (option) => ({
        id: parseInt(option.value),
        name: option.text,
      })
    );
    setFormData((prev) => ({
      ...prev,
      selectedFaskes: selectedOptions,
    }));
  };

  // Initialize map
  useEffect(() => {
    // Remove map if already exists
    if (L.DomUtil.get("map") !== null) {
      L.DomUtil.get("map")._leaflet_id = null;
    }

    const newMap = L.map("map").setView([coords.lat, coords.lng], 15);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(
      newMap
    );
    const newMarker = L.marker([coords.lat, coords.lng]).addTo(newMap);
    setMapObj({ instance: newMap, marker: newMarker });

    return () => {
      newMap.remove();
    };
  }, []);

  // Update map when coordinates change
  useEffect(() => {
    if (mapObj) {
      mapObj.instance.setView([coords.lat, coords.lng], 15);
      mapObj.marker.setLatLng([coords.lat, coords.lng]);
    }
  }, [coords]);

  // Reverse geocode
  useEffect(() => {
    const fetchAddress = async () => {
      try {
        const res = await fetch(
          `https://nominatim.openstreetmap.org/reverse?lat=${coords.lat}&lon=${coords.lng}&format=json`
        );
        const data = await res.json();
        setAddress(data.display_name || "");
      } catch {
        setAddress("Gagal mendapatkan alamat");
      }
    };
    fetchAddress();
  }, [coords]);

  const handleCoordsChange = (e) => {
    const val = e.target.value;
    const parts = val.split(",");
    if (parts.length === 2) {
      const lat = parseFloat(parts[0].trim());
      const lng = parseFloat(parts[1].trim());
      if (!isNaN(lat) && !isNaN(lng)) {
        setCoords({ lat, lng });
      }
    }
  };

  // Form validation
  const validateForm = () => {
    if (!formData.nama.trim()) {
      setError("Nama dokter wajib diisi");
      return false;
    }

    // Validate schedules
    const validSchedules = schedules.filter(
      (schedule) => schedule.day && schedule.start && schedule.end
    );

    if (validSchedules.length === 0) {
      setError("Minimal satu jadwal praktik harus diisi");
      return false;
    }

    // Check for invalid schedules (start time should be before end time)
    for (const schedule of validSchedules) {
      if (schedule.start >= schedule.end) {
        setError("Jam mulai harus lebih awal dari jam selesai");
        return false;
      }
    }

    return true;
  };

  // Form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError("");
    setSuccess("");

    try {
      // Transform schedules to match backend model
      const validSchedules = schedules
        .filter((schedule) => schedule.day && schedule.start && schedule.end)
        .map((schedule) => ({
          hari: schedule.day,
          jam_mulai: schedule.start,
          jam_selesai: schedule.end,
        }));

      // Prepare doctor data
      const dokterData = {
        nama: formData.nama,
        jadwal_praktik: validSchedules,
        consultation_url: formData.consultationUrl,
        // Tambahkan faskes IDs
        faskes_ids: formData.selectedFaskes.map((faskes) => faskes.id), // Kirim array ID faskes
      };

      console.log("Sending dokter data:", dokterData);

      // Make API call
      const response = await api.post("/dokter", dokterData);

      console.log("API Response:", response);

      if (response.status === 201) {
        setSuccess("Dokter berhasil ditambahkan!");

        // Reset form
        setFormData({
          nama: "",
          consultationUrl: "",
          selectedFaskes: [],
        });
        setSchedules([{ day: "", start: "", end: "" }]);
        setCoords({ lat: -6.2, lng: 106.816666 });
        setAddress("");
        logAdminActivity(
          "admin",
          "add_dokter",
          "dokter",
          response.data.ID || response.data.id,
          "success",
          `Dokter ${response.data.nama} berhasil ditambahkan`,
          response.data.nama,
          
        );
        // Redirect after success
        setTimeout(() => {
          navigate("/admin/faskes");
        }, 2000);
      }
    } catch (err) {
      console.error("Error adding dokter:", err);

      // Handle specific error responses
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.response?.status === 400) {
        setError("Data yang dikirim tidak valid. Periksa kembali form Anda.");
      } else if (err.response?.status === 401) {
        setError("Anda tidak memiliki akses untuk menambah dokter.");
      } else if (err.response?.status === 403) {
        setError("Token akses Anda tidak valid atau sudah kadaluarsa.");
      } else if (err.response?.status === 500) {
        setError("Terjadi kesalahan server. Silakan coba lagi.");
      } else if (err.code === "NETWORK_ERROR") {
        setError(
          "Tidak dapat terhubung ke server. Periksa koneksi internet Anda."
        );
      } else {
        setError("Gagal menambahkan dokter. Silakan coba lagi.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <AdminSidebar />
      <div className="flex-1 p-6 bg-gray-50">
        <AdminTopBar />

        <div className="bg-white p-6 rounded shadow">
          <button
            onClick={() => navigate(-1)}
            className="text-xl text-black font-semibold mb-8"
            disabled={loading}
          >
            &lt; Tambah Dokter
          </button>

          {/* Error Message */}
          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              {error}
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
              {success}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm block mb-1">
                  Nama Dokter <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nama"
                  value={formData.nama}
                  onChange={handleInputChange}
                  placeholder="Nama dokter"
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                />
              </div>
              <div>
                <label className="text-sm block mb-1">
                  URL Dokter dari Aplikasi Telekonsultasi
                </label>
                <input
                  type="text"
                  name="consultationUrl"
                  value={formData.consultationUrl}
                  onChange={handleInputChange}
                  placeholder="URL"
                  className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                />
              </div>

              {/* Faskes Selection */}
              <div className="md:col-span-2">
                <label className="text-sm block mb-1">
                  Faskes Tempat Praktik (Opsional)
                </label>
                {loadingFaskes ? (
                  <div className="text-sm text-gray-500">
                    Memuat daftar faskes...
                  </div>
                ) : (
                  <select
                    multiple
                    onChange={handleFaskesSelection}
                    className="w-full border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                    size="4"
                  >
                    {faskesList.map((faskes) => (
                      <option
                        key={faskes.ID || faskes.id}
                        value={faskes.ID || faskes.id}
                      >
                        {faskes.name || faskes.Nama || "Nama tidak tersedia"}
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Tahan Ctrl (atau Cmd di Mac) untuk memilih multiple faskes
                </p>
                {formData.selectedFaskes.length > 0 && (
                  <div className="mt-2">
                    <span className="text-sm font-medium">Dipilih: </span>
                    <span className="text-sm">
                      {formData.selectedFaskes.map((f) => f.name).join(", ")}
                    </span>
                  </div>
                )}
              </div>

              <div className="md:col-span-2">
                <label className="text-sm block mb-1">
                  Tempat Praktik (koordinat)
                </label>
                <input
                  type="text"
                  placeholder="Contoh: -6.2, 106.816666"
                  onChange={handleCoordsChange}
                  className="w-full border rounded px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  disabled={loading}
                />
                <label className="text-sm block mb-1">Alamat</label>
                <input
                  type="text"
                  value={address}
                  disabled
                  className="w-full border rounded px-3 py-2 text-sm mb-4 bg-gray-100"
                />
                <div id="map" className="w-full h-64 rounded"></div>
              </div>
            </div>

            <div className="mt-6">
              <label className="text-sm block mb-2 font-medium">
                Jadwal Praktik <span className="text-red-500">*</span>
              </label>
              {schedules.map((schedule, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2 items-center"
                >
                  <select
                    value={schedule.day}
                    onChange={(e) =>
                      handleScheduleChange(index, "day", e.target.value)
                    }
                    className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  >
                    <option value="">Pilih hari</option>
                    {days.map((day) => (
                      <option key={day} value={day}>
                        {day}
                      </option>
                    ))}
                  </select>
                  <input
                    type="time"
                    value={schedule.start}
                    onChange={(e) =>
                      handleScheduleChange(index, "start", e.target.value)
                    }
                    className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                  <input
                    type="time"
                    value={schedule.end}
                    onChange={(e) =>
                      handleScheduleChange(index, "end", e.target.value)
                    }
                    className="border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    disabled={loading}
                  />
                  {schedules.length > 1 && (
                    <button
                      type="button"
                      onClick={() => handleRemoveSchedule(index)}
                      className="text-red-500 text-xl px-2 hover:bg-red-50 rounded"
                      disabled={loading}
                    >
                      <img
                        src="/icon-trash.png"
                        alt="Delete"
                        className="h-5 w-5"
                      />
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                onClick={handleAddSchedule}
                className="mt-2 bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600 disabled:opacity-50"
                disabled={loading}
              >
                + Tambah Jadwal
              </button>
              <p className="text-xs text-gray-500 mt-1">
                Minimal satu jadwal praktik harus diisi
              </p>
            </div>

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="bg-gray-500 text-white px-6 py-2 rounded hover:bg-gray-600 disabled:opacity-50"
                disabled={loading}
              >
                Batal
              </button>
              <button
                type="submit"
                className="bg-blue-500 text-white px-6 py-2 rounded hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                disabled={loading}
              >
                {loading ? "Menyimpan..." : "Simpan"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
